import { Controller } from "@hotwired/stimulus";

/**
 * GO4S Design System — Modale / overlay.
 * Ouvre/ferme l'overlay, ferme sur Échap + clic sur le fond, verrouille le scroll.
 * A11y : piège le focus dans le dialog (Tab/Shift+Tab), restaure le focus au
 * déclencheur à la fermeture, et inerte le reste de la page pendant l'ouverture.
 * Spec : docs/design-system/REFERENCE.md §4
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default class extends Controller {
  static targets = ["overlay", "panel"];
  // openOnConnect : pour les modales pilotées par turbo-frame (rendues déjà
  // visibles, sans déclencheur) — active Échap/backdrop/focus dès l'arrivée.
  static values = { openOnConnect: { type: Boolean, default: false } };

  connect() {
    this.onKey = this.onKey.bind(this);
    // Compat : ferme sur l'évènement global legacy `modal:close` (boutons internes
    // de corps de modale) et auto-ferme après une soumission Turbo réussie.
    this.onExternalClose = this.close.bind(this);
    this.onSubmitEnd = this.handleSubmitEnd.bind(this);
    window.addEventListener("modal:close", this.onExternalClose);
    // Opened from elsewhere (modal-opener) when its root carries an id.
    this.onExternalOpen = (e) => {
      if (this.element.id && e.detail && e.detail.id === this.element.id) this.open();
    };
    window.addEventListener("ds-modal:open", this.onExternalOpen);
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd);
    if (this.openOnConnectValue) this.open();
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKey);
    window.removeEventListener("modal:close", this.onExternalClose);
    window.removeEventListener("ds-modal:open", this.onExternalOpen);
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd);
    this.releaseBodyLock();
    this.releaseInert();
  }

  handleSubmitEnd(e) {
    if (e.detail && e.detail.success) this.close();
  }

  open() {
    // A window opened from a ⋮ menu: the menu closes (menus listen to Escape;
    // windows ignore this synthetic one).
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
    // Mémorise l'élément qui avait le focus pour le lui rendre à la fermeture.
    this.previouslyFocused = document.activeElement;
    this.overlayTarget.hidden = false;
    document.body.classList.add("ds-modal-open");
    document.addEventListener("keydown", this.onKey);
    this.applyInert();
    // Focalise le premier élément interactif du corps si présent, sinon le
    // panneau (dialog) lui-même — évite l'anneau natif sur la croix à l'ouverture.
    const body = this.panelTarget.querySelector(".ds-modal__body");
    const first = body && body.querySelector(FOCUSABLE);
    (first || this.panelTarget).focus({ preventScroll: true });
  }

  close() {
    this.overlayTarget.hidden = true;
    this.releaseBodyLock();
    document.removeEventListener("keydown", this.onKey);
    this.releaseInert();
    // Rend le focus au déclencheur (s'il est encore dans le DOM et visible).
    if (this.previouslyFocused && this.previouslyFocused.isConnected) {
      this.previouslyFocused.focus({ preventScroll: true });
    }
    this.previouslyFocused = null;
    this.forgetOpeningParams();
    // Lets a page react to any way of closing (button, Escape, backdrop).
    this.dispatch("closed");
  }

  // A window opened by the address (?edit_course=…, ?new=1…) leaves it once
  // closed: reloading the page must not open it again (recette 02/11/2026).
  forgetOpeningParams() {
    const url = new URL(window.location.href);
    const keys = ["add_course", "edit_course", "show_course", "section", "new", "edit"];
    if (!keys.some((key) => url.searchParams.has(key))) return;

    keys.forEach((key) => url.searchParams.delete(key));
    window.history.replaceState(window.history.state, "", url.toString());
  }

  // The page stays locked while another window is still open (a quick add
  // closing over the course window).
  releaseBodyLock() {
    const open = document.querySelectorAll('[data-ds-modal-target="overlay"]:not([hidden])');
    if (open.length === 0) document.body.classList.remove("ds-modal-open");
  }

  backdrop(e) {
    if (e.target === this.overlayTarget) this.close();
  }

  onKey(e) {
    // The Escape modal-opener sends to close the dropdown menus is synthetic:
    // it must not close the window the quick add opens over (recette 02/11/2026).
    if (!e.isTrusted) return;
    // A window opened over another one (a quick add over the course window):
    // only the top one answers Escape and Tab.
    const open = Array.from(document.querySelectorAll('[data-ds-modal-target="overlay"]:not([hidden])'));
    if (open.length > 1 && open[open.length - 1] !== this.overlayTarget) return;
    if (e.key === "Escape") return this.close();
    if (e.key === "Tab") this.trapFocus(e);
  }

  // Menu SlimSelect (ou autre popover) détaché au <body> et actuellement
  // ouvert : ses éléments sont hors du panel mais font partie de la modale.
  detachedPopover() {
    return document.querySelector(".ss-content.ss-open, .ss-content.ss-open-below, .ss-content.ss-open-above");
  }

  // Confine la tabulation aux éléments focusables du panneau (boucle).
  trapFocus(e) {
    // Si un popover détaché (SlimSelect) est ouvert, laisser le focus y circuler
    // librement : le renvoyer dans le panel fermerait le menu.
    if (this.detachedPopover()) return;
    const items = this.panelTarget.querySelectorAll(FOCUSABLE);
    if (!items.length) {
      e.preventDefault();
      this.panelTarget.focus({ preventScroll: true });
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === this.panelTarget)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Popovers détachés au <body> par leurs libs (SlimSelect, flatpickr) : ils
  // appartiennent logiquement aux champs de la modale. Les inerter rendrait
  // leurs options/dates non cliquables — on les exclut donc de applyInert().
  POPOVER_SELECTOR = ".ss-content, .flatpickr-calendar";

  // Inerte tous les frères du root de la modale pendant l'ouverture, pour que
  // le lecteur d'écran et le focus ne « fuient » pas vers le fond.
  applyInert() {
    const root = this.element;
    this.inerted = [];
    Array.from(document.body.children).forEach((el) => {
      if (el === root || el.contains(root)) return;
      if (el.hasAttribute("inert")) return;
      // Ne pas inerter les menus déroulants détachés (select/date) : ils font
      // partie de la modale même s'ils sont montés directement sous <body>.
      if (el.matches && el.matches(this.POPOVER_SELECTOR)) return;
      el.setAttribute("inert", "");
      this.inerted.push(el);
    });
  }

  releaseInert() {
    (this.inerted || []).forEach((el) => el.removeAttribute("inert"));
    this.inerted = [];
  }
}
